import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { clientBookingService, BookingDetailData } from '@/services/clientBookingService';
import { BookingDetailSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace('IDR', 'Rp.');
};

const formatIndonesianDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const weekday = date.toLocaleDateString('id-ID', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleDateString('id-ID', { month: 'short' });
  const year = date.getFullYear();
  return `${weekday}, ${day} ${month} ${year}`;
};

const ClientBookingDetail = () => {
  const { invoice_number } = useParams<{ invoice_number: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BookingDetailData | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!invoice_number) {
        setError('Invoice number is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await clientBookingService.getBookingDetail(invoice_number);

        if (response.success) {
          setData(response.data);
        } else {
          setError(response.message || 'Gagal memuat detail booking');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Gagal memuat detail booking');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [invoice_number]);

  const handleContactArena = () => {
    if (!data) return;

    if (data.arena_info.whatsapp) {
      const message = encodeURIComponent(
        `Halo, saya ingin bertanya tentang booking ${invoice_number} di ${data.arena_info.name}`
      );
      void openExternalUrl(`https://wa.me/${data.arena_info.whatsapp}?text=${message}`);
    } else if (data.arena_info.email) {
      const subject = encodeURIComponent(`Pertanyaan tentang booking ${invoice_number}`);
      const body = encodeURIComponent(
        `Halo, saya ingin bertanya tentang booking saya di ${data.arena_info.name}.`
      );
      window.location.href = `mailto:${data.arena_info.email}?subject=${subject}&body=${body}`;
    }
  };

  const handleOpenMaps = () => {
    if (data?.arena_info.maps_link) {
      void openExternalUrl(data.arena_info.maps_link);
    }
  };

  if (loading) {
    return <BookingDetailSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="bg-sporto-bg min-h-screen p-4 md:p-6 pt-20">
        <div className="max-w-xl mx-auto bg-white rounded-3xl p-8 text-center border border-[#E0E6ED] shadow-2xs space-y-4">
          <Icon icon="ph:warning-circle-bold" className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-lg font-bold text-[#0E1726]">Gagal Memuat Detail</h2>
          <p className="text-xs text-gray-500">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/booking')}
            className="h-10 px-5 rounded-full bg-sporto-orange text-white text-xs font-bold hover:bg-sporto-orange-hover cursor-pointer"
          >
            Kembali ke Daftar Booking
          </button>
        </div>
      </div>
    );
  }

  const isUnpaid =
    data.payment_info.payment_status?.toLowerCase() === 'unpaid' ||
    data.payment_info.payment_status?.toLowerCase() === 'pending';

  const firstBooking = data.field_bookings?.[0];

  // Calculate field cost breakdown
  const fieldTotalPrice = data.field_bookings.reduce((sum, f) => sum + f.price, 0);
  const totalAmount = data.payment_info.total_amount;
  const totalDurationMinutes = data.field_bookings.reduce((sum, f) => sum + f.duration, 0);
  const durationHours = Math.max(1, Math.round(totalDurationMinutes / 60));

  // Additional fee estimation
  const serviceFee = totalAmount > fieldTotalPrice ? Math.min(15000, totalAmount - fieldTotalPrice) : 0;
  const paymentFee = totalAmount > fieldTotalPrice + serviceFee ? totalAmount - fieldTotalPrice - serviceFee : 0;

  return (
    <div className="bg-sporto-bg min-h-screen text-sporto-dark py-5">
      {/* Desktop Breadcrumb Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:block bg-white border-b border-[#E0E6ED] px-6 py-4 mb-6 shadow-2xs">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/booking')}
            className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#0E1726]">Detail Booking</h1>
            <p className="text-xs text-gray-500">{data.invoice_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-18 md:pt-2 space-y-5">
        {/* 1. Header Radial Gradient Dark Card */}
        <div
          className="shadow-md text-white overflow-hidden transition-all"
          style={{
            borderRadius: '18px',
            padding: '16px',
            background: 'radial-gradient(120% 90% at 100% 0%, rgb(37, 49, 74) 0%, rgb(14, 23, 38) 65%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          {/* Top Row: Kode Booking & Status */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider block mb-0.5">
                KODE BOOKING
              </span>
              <span className="text-lg md:text-xl font-bold text-white">
                {data.invoice_number}
              </span>
            </div>

            {isUnpaid ? (
              <div className="bg-amber-950/70 text-amber-400 border border-amber-500/30 text-xs font-bold px-3 py-1 rounded-md">
                UNPAID
              </div>
            ) : (
              <div className="bg-emerald-950/70 text-emerald-400 border border-emerald-500/30 text-xs font-bold px-3 py-1 rounded-md">
                PAID
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-white/10" />

          {/* Arena Info Row */}
          <div className="flex items-center gap-3">
            {data.arena_info.image ? (
              <img
                src={data.arena_info.image}
                alt={data.arena_info.name}
                className="w-12 h-12 rounded-xl object-cover shrink-0 border border-white/10"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-gray-300 shrink-0">
                <Icon icon="ph:stadium-bold" className="w-6 h-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-white truncate leading-snug">
                {data.arena_info.name}
              </h2>
              <p className="text-xs text-gray-300 truncate mt-0.5">
                {data.arena_info.address || 'Kota Yogyakarta · 2,4 km dari kamu'}
              </p>
            </div>
          </div>
        </div>

        {/* 2. Specification Table Card */}
        <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
          {/* Tanggal */}
          <div className="flex items-center justify-between p-4 text-xs">
            <div className="flex items-center gap-2.5 text-gray-400 font-medium">
              <Icon icon="ph:calendar-blank" className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Tanggal</span>
            </div>
            <span className="font-bold text-[#0E1726]">
              {firstBooking ? formatIndonesianDate(firstBooking.booking_date) : '-'}
            </span>
          </div>

          {/* Jam main */}
          <div className="flex items-center justify-between p-4 text-xs">
            <div className="flex items-center gap-2.5 text-gray-400 font-medium">
              <Icon icon="ph:clock" className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Jam main</span>
            </div>
            <span className="font-bold text-[#0E1726]">
              {firstBooking ? (formatLocalEventTimeRange(firstBooking.start_time, firstBooking.end_time) || '-') : '-'}
            </span>
          </div>

          {/* Lapangan */}
          <div className="flex items-center justify-between p-4 text-xs">
            <div className="flex items-center gap-2.5 text-gray-400 font-medium">
              <Icon icon="ph:dribbble" className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Lapangan</span>
            </div>
            <span className="font-bold text-[#0E1726]">
              {data.field_bookings.map((f) => f.field_name).join(', ') || 'Lapangan A'}
            </span>
          </div>

          {/* Durasi */}
          <div className="flex items-center justify-between p-4 text-xs">
            <div className="flex items-center gap-2.5 text-gray-400 font-medium">
              <Icon icon="ph:hourglass-medium" className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Durasi</span>
            </div>
            <span className="font-bold text-[#0E1726]">{durationHours} jam</span>
          </div>

          {/* Atas nama */}
          <div className="flex items-center justify-between p-4 text-xs">
            <div className="flex items-center gap-2.5 text-gray-400 font-medium">
              <Icon icon="ph:user" className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Atas nama</span>
            </div>
            <span className="font-bold text-[#0E1726]">{data.booker_info.name}</span>
          </div>
        </div>

        {/* 3. Rincian Biaya */}
        <div>
          <h3 className="text-base font-bold text-[#0E1726] mb-3">Rincian biaya</h3>
          <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs p-4 space-y-3.5">
            {/* Sewa Lapangan */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 font-medium">Sewa lapangan ({durationHours} jam)</span>
              <span className="font-bold text-[#0E1726]">
                {formatCurrency(fieldTotalPrice)}
              </span>
            </div>

            {/* Biaya Layanan */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 font-medium">Biaya layanan</span>
              <span className="font-bold text-[#0E1726]">
                {formatCurrency(serviceFee)}
              </span>
            </div>

            {/* Biaya Pembayaran */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 font-medium">Biaya pembayaran</span>
              <span className="font-bold text-[#0E1726]">
                {formatCurrency(paymentFee)}
              </span>
            </div>

            {/* Promo */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 font-medium">Promo SPORTO10</span>
              <span className="font-bold text-emerald-600">- Rp. 0</span>
            </div>

            {/* Divider Line & Total */}
            <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
              <span className="text-sm font-bold text-[#0E1726]">Total</span>
              <span className="text-lg font-bold text-[#FC480D]">
                {formatCurrency(totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* 4. Aturan Venue */}
        <div>
          <h3 className="text-base font-bold text-[#0E1726] mb-2">Aturan venue</h3>
          <div className="flex items-start gap-2 text-xs text-[#556575] leading-relaxed">
            <Icon icon="ph:clock" className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
            <span>Datang maksimal 15 menit sebelum jam main untuk konfirmasi ke petugas venue.</span>
          </div>
        </div>

        {/* 5. Footer Actions */}
        <div className="space-y-3 pt-3">
          {isUnpaid && (
            <button
              type="button"
              onClick={() => navigate(`/event/checkout/${data.invoice_number}`)}
              className="w-full h-11 rounded-full bg-[#FC480D] hover:bg-[#D83B08] active:scale-95 text-white text-sm font-bold flex items-center justify-center gap-2 shadow-md cursor-pointer transition-all"
            >
              <Icon icon="ph:credit-card-bold" className="w-4 h-4" />
              <span>Bayar Sekarang</span>
            </button>
          )}

          <div className="flex items-center gap-3">
            {data.arena_info.maps_link && (
              <button
                type="button"
                onClick={handleOpenMaps}
                className="flex-1 h-11 rounded-full border border-gray-300 bg-white text-[#0E1726] hover:bg-gray-50 text-xs font-bold flex items-center justify-center gap-2 cursor-pointer shadow-2xs transition-colors"
              >
                <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-blue-600" />
                <span>Petunjuk Lokasi</span>
              </button>
            )}

            {(data.arena_info.whatsapp || data.arena_info.email) && (
              <button
                type="button"
                onClick={handleContactArena}
                className="flex-1 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer shadow-2xs transition-colors"
              >
                <Icon icon="ph:whatsapp-logo-bold" className="w-4 h-4" />
                <span>Hubungi Pengelola</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientBookingDetail;
